import { FC, useEffect, useState } from 'react';
import { Box, Checkbox, Typography } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { Player } from '@lottiefiles/react-lottie-player';
import { APPLICATION_STORES, DataAccessProvider, MMP_SOURCE_MAP } from '@constants/constants';
import { processRatingString } from '@utils/common';
import UserAuth from '@services/auth';
import GameDetails, { IGameDetails } from '../GameData/GameDetails';
import { CompanyDetailsProps } from '../../types';
import DataSourceOverview from './DataSourceOverview';
import pvxIntelLoader from '../../../../../assets/images/PvxIntelLoader.json';
import dayjs from 'dayjs';
import ProcessingDataAccess from '../DataAccess/ProcessingDataAccess';

// Configuration for NDA form fields display
const NDA_FORM_FIELDS = [
  { label: 'Company Name', key: 'companyName' },
  { label: "Signing Authority's Name", key: 'signingAuthorityName' },
  { label: 'Company Address', key: 'companyAddress' },
  {
    label: "Signing Authority's Email",
    key: 'signingAuthorityEmail',
  },
  {
    label: "Signing Authority's Title",
    key: 'signingAuthorityTitle',
  },
  // Note: NDA Document download field is commented out in original
  // { label: "NDA Document", key: "ndaDocument" },
];

// Interface for NDA information structure
interface NDAInformation {
  companyName: string;
  companyAddress: string;
  signingAuthorityName: string;
  signingAuthorityEmail: string;
  signingAuthorityTitle: string;
}

// Interface for application information structure
interface ApplicationInformation {
  category: string | null;
  genre: string | null;
  selfServeId: string;
  requestStatus: string;
  accessEmail: string;
  mmp: string;
}

/**
 * ApplicationOverview Component
 * Displays comprehensive company information including:
 * - Game details for Play Store and App Store applications
 * - NDA (Non-Disclosure Agreement) information
 * - Data integration configuration
 * - Monthly data refresh authorization
 */
const ApplicationOverview: FC<CompanyDetailsProps> = ({ data: companyData }) => {
  // State for storing NDA-related information
  const [ndaInformation, setNDAInformation] = useState<NDAInformation | null>(null);

  // State for storing loader visibility
  const [isPvXInteligenceLoading, setPvXInteligenceLoading] = useState<boolean>(false);

  // State for storing application details and metadata
  const [applicationInfo, setApplicationInfo] = useState<ApplicationInformation | null>(null);

  // State for storing Play Store game details
  const [playStoreGameDetails, setPlayStoreGameDetails] = useState<IGameDetails | null>(null);

  // State for storing App Store game details
  const [appStoreGameDetails, setAppStoreGameDetails] = useState<IGameDetails | null>(null);

  /**
   * Processes and updates application metadata for both app stores
   * Parses JSON metadata and extracts game details for Play Store and App Store
   * Also processes rating strings to ensure consistent format
   */
  const updateApplicationMetadata = (metadata: string) => {
    const parsedMetadata = JSON.parse(metadata);

    const playStoreData: IGameDetails = parsedMetadata[APPLICATION_STORES.PLAY_STORE] || null;
    const appStoreData: IGameDetails = parsedMetadata[APPLICATION_STORES.APP_STORE] || null;

    // Process rating strings to ensure consistent format
    if (playStoreData?.rating) {
      playStoreData.rating = processRatingString(playStoreData.rating as string);
    }

    if (appStoreData?.rating) {
      appStoreData.rating = processRatingString(appStoreData.rating as string);
    }

    setPlayStoreGameDetails(playStoreData);
    setAppStoreGameDetails(appStoreData);
  };

  /**
   * Updates NDA details if all required fields are present
   * Sets NDA information to null if any required field is missing
   */
  const updateNDAInformation = (
    companyName: string | undefined,
    companyAddress: string | undefined,
    signingAuthorityName: string | undefined,
    signingAuthorityEmail: string | undefined,
    signingAuthorityTitle: string | undefined,
  ) => {
    // Validate that all required NDA fields are present
    const allFieldsPresent =
      companyName &&
      companyAddress &&
      signingAuthorityName &&
      signingAuthorityEmail &&
      signingAuthorityTitle;

    if (!allFieldsPresent) {
      setNDAInformation(null);
    } else {
      setNDAInformation({
        companyName,
        companyAddress,
        signingAuthorityName,
        signingAuthorityEmail,
        signingAuthorityTitle,
      });
    }
  };

  /**
   * Effect hook to initialize component state when company data changes
   * Processes and sets application information, NDA details, and metadata
   */
  useEffect(() => {
    if (!companyData) return;

    // Set basic application information
    setApplicationInfo({
      selfServeId: companyData.id,
      category: companyData.category,
      genre: companyData.genre,
      requestStatus: companyData.requestStatus,
      accessEmail: companyData.accessEmail || '',
      mmp: companyData.mmp,
    });

    // Update NDA information from company data
    updateNDAInformation(
      companyData.companyName,
      companyData.companyAddress,
      companyData.signingAuthorityName,
      companyData.signingAuthorityEmail,
      companyData.signingAuthorityTitle,
    );

    // Process application metadata for game details
    updateApplicationMetadata(companyData.metadata);
  }, [companyData]);

  /**
   * Effect hook to set initial loading state for PvX Intelligence
   * Simulates a loading delay of 2 seconds before setting the loading state
   */
  useEffect(() => {
    setPvXInteligenceLoading(true);
  }, []);

  /**
   * Effect hook to redirect to /dashboard 8 seconds from when isPvXInteligenceLoading is set to true
   * This simulates a delay before redirecting the user to the dashboard
   */
  useEffect(() => {
    if (isPvXInteligenceLoading) {
      UserAuth.setDemoToken(new Date().toISOString());
      const timer = setTimeout(() => {
        window.location.href = '/dashboard';
      }, 6000);
      return () => clearTimeout(timer);
    }
  }, [isPvXInteligenceLoading]);

  /**
   * Handles downloading the NDA attachment
   */
  const handleNDAAttachmentDownload = async () => {
    return null;
  };

  /**
   * Constructs data source overview data for display
   */
  const getDataSourceOverviewData = (companyData: any) => {
    if (companyData.mmp === DataAccessProvider.APPS_FLYER) {
      return {
        dataSource: MMP_SOURCE_MAP[companyData.mmp as DataAccessProvider],
        accountName: companyData.accountName || '',
        iapRevenueMargin: companyData.iapMargin || '',
        // apiToken: companyData.apiToken || "",
        // monthlyRefresh: companyData.monthlyRefresh || "",
      };
    } else if (companyData.mmp === DataAccessProvider.ADJUST) {
      return {
        dataSource: MMP_SOURCE_MAP[companyData.mmp as DataAccessProvider],
        accountName: companyData.accountName || '',
        iapRevenueMargin: companyData.iapMargin || '',
        iaaIdList: JSON.parse(companyData?.dataSourceMetadata || '{}')?.iaaIdList || [],
        // monthlyRefresh: companyData.monthlyRefresh || "",
      };
    } else if (companyData?.connectionDetails) {
      return {
        dataSource: MMP_SOURCE_MAP[companyData.mmp as DataAccessProvider],
        ...JSON.parse(companyData.connectionDetails),
        // monthlyRefresh: companyData.monthlyRefresh || "",
      };
    } else {
      return {};
    }
  };

  // Check if current or last month
  const isLastToLastMonth = (date: string) => {
    const lastMonth = dayjs().subtract(1, 'month').format('YYYY-MM');
    const currentMonth = dayjs().format('YYYY-MM');
    return date === lastMonth || date === currentMonth;
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
      }}
    >
      {isPvXInteligenceLoading ? (
        <>
          <ProcessingDataAccess />
        </>
      ) : (
        <>
          {/* Game Details Section - Display app store information */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: '1rem',
            }}
          >
            {playStoreGameDetails && (
              <GameDetails
                gameDetails={playStoreGameDetails}
                setIsEditGameOpen={() => {}}
                category={applicationInfo?.category}
                genre={applicationInfo?.genre}
              />
            )}
            {appStoreGameDetails && (
              <GameDetails
                gameDetails={appStoreGameDetails}
                setIsEditGameOpen={() => {}}
                category={applicationInfo?.category}
                genre={applicationInfo?.genre}
              />
            )}
          </Box>

          {/* NDA Information Section */}
          <Box
            sx={{
              border: '2px solid #2F736E1F',
              borderRadius: '1rem',
              padding: '1.25rem',
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '1rem',
            }}
          >
            {NDA_FORM_FIELDS.map(({ label, key }) => (
              <Box key={key}>
                <Typography fontSize="0.75rem" lineHeight="1rem" fontWeight={700} color="#6C6C6C">
                  {label}
                </Typography>

                {/* Special handling for NDA document download */}
                {key === 'ndaDocument' ? (
                  <Typography
                    fontSize="0.875rem"
                    lineHeight="1.25rem"
                    fontWeight={700}
                    color="#7FBCAD"
                    display="flex"
                    alignItems="center"
                    sx={{
                      cursor: 'pointer',
                      gap: '0.5rem',
                    }}
                    onClick={(e) => {
                      handleNDAAttachmentDownload();
                      e.stopPropagation();
                    }}
                  >
                    Download
                    <FileDownloadIcon
                      sx={{
                        width: '1rem',
                        height: '1rem',
                        color: '#7FBCAD',
                      }}
                    />
                  </Typography>
                ) : (
                  <Typography
                    fontSize="0.875rem"
                    lineHeight="1.25rem"
                    fontWeight={700}
                    color="#6C6C6C"
                  >
                    {ndaInformation?.[key as keyof NDAInformation] || 'NA'}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>

          {/* Data Integration Details Section */}
          <Box
            sx={{
              border: '2px solid #2F736E1F',
              borderRadius: '1rem',
              padding: '1.25rem',
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
            }}
          >
            <Box
              sx={{
                width: '100%',
                flex: 1,
                fontSize: '1rem',
                lineHeight: '1.5rem',
                fontWeight: 700,
                color: '#162936',
              }}
            >
              Data Integration Details
            </Box>
            <DataSourceOverview
              source={companyData.mmp as DataAccessProvider}
              data={getDataSourceOverviewData(companyData)}
            />
          </Box>

          {/* Monthly Data Refresh Authorization Section */}
          <Box
            sx={{
              border: '2px solid #2F736E1F',
              borderRadius: '1rem',
              padding: '1.25rem',
              display: 'flex',
              flexDirection: 'row',
              gap: '0.5rem',
              alignItems: 'center',
            }}
          >
            <Checkbox
              checked={true}
              onChange={() => {}}
              sx={{
                color: '#7FBCAD',
              }}
            />
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.25rem',
              }}
            >
              <Typography
                fontSize="0.875rem"
                lineHeight="1.25rem"
                fontWeight={700}
                color="#162936"
                letterSpacing="-0.35px"
              >
                I authorise PvX to securely refresh my data each month for accurate reporting.
              </Typography>
              <Typography fontSize="0.875rem" lineHeight="1.25rem" fontWeight={500} color="#6C6C6C">
                Dashboards will be refreshed monthly by the 10th of each month.
              </Typography>
            </Box>
          </Box>

          {/* Data Status Section */}
          <Box
            sx={{
              backgroundColor: '#fff',
              padding: '1.5rem 1.25rem',
              borderRadius: '1rem',
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
              border: '2px solid #2F736E1F',
            }}
          >
            <Typography
              sx={{
                color: '#162936',
                fontFamily: 'Manrope, sans-serif',
                fontWeight: 700,
                fontSize: '1rem',
                lineHeight: '1.375rem',
              }}
            >
              Data Status
            </Typography>
            {companyData?.gameRevenueDetailsDto?.maxTransactionPeriod ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexDirection: 'row',
                  gap: '0.5rem',
                  borderBottom: '1px solid #E8EAEB',
                  paddingBottom: '1rem',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: !isLastToLastMonth(
                      companyData?.gameRevenueDetailsDto?.maxTransactionPeriod,
                    )
                      ? '#FF6347'
                      : '#2F736E',
                    borderRadius: '0.25rem',
                    width: '0.75rem',
                    height: '0.75rem',
                  }}
                />
                <Typography
                  sx={{
                    color: '#000',
                    fontFamily: 'Manrope, sans-serif',
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    lineHeight: '1rem',
                  }}
                >
                  {!isLastToLastMonth(companyData?.gameRevenueDetailsDto?.maxTransactionPeriod)
                    ? 'Not Updated'
                    : 'Updated'}{' '}
                  • last transaction month{' '}
                  {dayjs(companyData?.gameRevenueDetailsDto?.maxTransactionPeriod).format(
                    'MMM YYYY',
                  )}
                </Typography>
              </Box>
            ) : (
              <></>
            )}
            <Box
              sx={{
                width: '13rem',
                display: 'flex',
                flexDirection: 'column',
                gap: '1rem',
              }}
            >
              <Typography
                sx={{
                  color: '#162936',
                  fontFamily: 'Manrope, sans-serif',
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  lineHeight: '1rem',
                  display: 'flex',
                  justifyContent: 'space-between',
                }}
              >
                Cohort Start Month{' '}
                <span style={{ fontWeight: 700 }}>
                  {companyData?.gameRevenueDetailsDto?.cohortStartMonth || '-'}
                </span>
              </Typography>
              <Typography
                sx={{
                  color: '#162936',
                  fontFamily: 'Manrope, sans-serif',
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  lineHeight: '1rem',
                  display: 'flex',
                  justifyContent: 'space-between',
                }}
              >
                Cohort End Month{' '}
                <span style={{ fontWeight: 700 }}>
                  {companyData?.gameRevenueDetailsDto?.cohortEndMonth || '-'}
                </span>
              </Typography>
            </Box>
            <Box
              sx={{
                borderTop: '1px solid #E8EAEB',
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr',
                paddingTop: '1rem',
              }}
            >
              <Typography
                sx={{
                  color: '#162936',
                  fontFamily: 'Manrope, sans-serif',
                  fontWeight: 700,
                  fontSize: '0.875rem',
                  lineHeight: '1rem',
                }}
              >
                Data split by Channel
                <Checkbox
                  icon={<RadioButtonUncheckedIcon />}
                  checkedIcon={<CheckCircleOutlineIcon sx={{ color: '#7FBCAD' }} />}
                  checked={Boolean(companyData?.gameRevenueDetailsDto?.channelSplitAvailable)}
                  sx={{
                    padding: '0 0 0 0.5rem',
                    cursor: 'default',
                  }}
                />
              </Typography>
              <Typography
                sx={{
                  color: '#162936',
                  fontFamily: 'Manrope, sans-serif',
                  fontWeight: 700,
                  fontSize: '0.875rem',
                  lineHeight: '1rem',
                }}
              >
                Data split by Country
                <Checkbox
                  icon={<RadioButtonUncheckedIcon />}
                  checkedIcon={<CheckCircleOutlineIcon sx={{ color: '#7FBCAD' }} />}
                  checked={Boolean(companyData?.gameRevenueDetailsDto?.countrySplitAvailable)}
                  sx={{
                    padding: '0 0 0 0.5rem',
                    cursor: 'default',
                  }}
                />
              </Typography>
              <Typography
                sx={{
                  color: '#162936',
                  fontFamily: 'Manrope, sans-serif',
                  fontWeight: 700,
                  fontSize: '0.875rem',
                  lineHeight: '1rem',
                }}
              >
                Data split by Platform
                <Checkbox
                  icon={<RadioButtonUncheckedIcon />}
                  checkedIcon={<CheckCircleOutlineIcon sx={{ color: '#7FBCAD' }} />}
                  checked={Boolean(companyData?.gameRevenueDetailsDto?.platformSplitAvailable)}
                  sx={{
                    padding: '0 0 0 0.5rem',
                    cursor: 'default',
                  }}
                />
              </Typography>
            </Box>
          </Box>
        </>
      )}

      {/* PvX Intelligence Loading Section */}
      {isPvXInteligenceLoading ? (
        <Box
          sx={{
            position: 'absolute',
            height: '100%',
            width: '100%',
            top: 0,
            left: 0,
            background: '#00000050',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              width: '100%',
              py: '0.5rem',
            }}
          >
            <Player
              src={pvxIntelLoader}
              className="player"
              loop
              autoplay
              style={{
                width: '17rem',
              }}
            />
          </Box>
        </Box>
      ) : (
        <></>
      )}
    </div>
  );
};

export default ApplicationOverview;
